import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, Shield } from 'lucide-react';
import { Button } from './ui/button';

const ServicesDropdown = () => {
  const [activeCategory, setActiveCategory] = useState('trademark');
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);
  const handleMouseEnter = () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); setIsOpen(true); };
  const handleMouseLeave = () => { timeoutRef.current = setTimeout(() => setIsOpen(false), 300); };

  const serviceCategories = {
    trademark: { title: 'Trademark', description: 'Comprehensive trademark protection', services: [
      { name: 'Search & Registration', desc: 'Protect your brand and logo', link: '/services/trademark/search-registration' },
      { name: 'Clearance Searches', desc: 'Verify availability before filing', link: '/services/trademark/clearance' },
      { name: 'Application Preparation', desc: 'Professional USPTO applications', link: '/services/trademark/application' },
      { name: 'Office Actions', desc: 'Respond to USPTO objections', link: '/services/trademark/office-actions' },
      { name: 'Monitoring', desc: 'Track potential infringements', link: '/services/trademark/monitoring' },
      { name: 'Statement Of Use', desc: 'Complete your ITU application', link: '/services/trademark/statement-of-use' },
      { name: 'Change of Ownership', desc: 'Transfer trademark rights', link: '/services/trademark/ownership-change' },
      { name: 'Portfolio Management', desc: 'Manage multiple trademarks', link: '/services/trademark/portfolio' }
    ]},
    copyright: { title: 'Copyright', description: 'Protect your creative works', services: [
      { name: 'Registration', desc: 'Official U.S. Copyright Office filing', link: '/services/copyright/registration' },
      { name: 'Enforcement', desc: 'Stop unauthorized use', link: '/services/copyright/enforcement' },
      { name: 'Assignments & Licensing', desc: 'Transfer and license rights', link: '/services/copyright/assignments' },
      { name: 'Litigation', desc: 'Federal court representation', link: '/services/copyright/litigation' },
      { name: 'Portfolio Management', desc: 'Manage creative assets', link: '/services/copyright/portfolio' },
      { name: 'DMCA Services', desc: 'Takedown services', link: '/services/copyright/dmca' }
    ]},
    patent: { title: 'Patent', description: 'Secure your innovations', services: [
      { name: 'Patent Searches', desc: 'Prior art analysis', link: '/services/patent/searches' },
      { name: 'Office Actions', desc: 'Overcome USPTO rejections', link: '/services/patent/office-actions' },
      { name: 'Applications', desc: 'Utility, design, provisional', link: '/services/patent/applications' },
      { name: 'Enforcement', desc: 'Protect against infringement', link: '/services/patent/enforcement' }
    ]},
    company: { title: 'Company Formation', description: 'Establish your business', services: [
      { name: 'LLC Formation', desc: 'Limited liability protection', link: '/services/company/llc' },
      { name: 'C Corporation', desc: 'For growth and investment', link: '/services/company/c-corp' },
      { name: 'S Corporation', desc: 'Pass-through taxation', link: '/services/company/s-corp' },
      { name: 'Non-Profit', desc: 'Tax-exempt formation', link: '/services/company/non-profit' }
    ]}
  };

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button className="flex items-center text-[#111C16] hover:text-[#C85A40] font-body text-sm font-medium transition-colors">
        Services <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} />
      </button>
      {isOpen && <div className="absolute left-0 right-0 h-4 top-full" />}
      <div className={`fixed left-0 right-0 top-16 bg-white border-t border-[#D5D1C8] shadow-lg z-50 transition-all duration-200 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className="max-w-7xl mx-auto flex">
          <div className="w-64 bg-[#1A3628] p-6">
            <p className="eyebrow !text-[#C85A40] mb-4">Practice Areas</p>
            <div className="space-y-1">
              {Object.entries(serviceCategories).map(([key, cat]) => (
                <button key={key} onMouseEnter={() => setActiveCategory(key)} className={`w-full text-left px-3 py-2.5 rounded-lg font-body text-sm transition-all ${activeCategory === key ? 'bg-[#C85A40] text-white font-medium' : 'text-white/70 hover:text-white hover:bg-white/10'}`}>
                  {cat.title}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 p-6 bg-white">
            <p className="font-display text-xl font-medium text-[#111C16] mb-1">{serviceCategories[activeCategory].title}</p>
            <p className="font-body text-sm text-[#4A5D53] mb-4">{serviceCategories[activeCategory].description}</p>
            <div className="grid grid-cols-2 gap-2">
              {serviceCategories[activeCategory].services.map((service, i) => (
                <Link key={i} to={service.link} onClick={() => setIsOpen(false)} className="flex items-start gap-3 p-3 rounded-xl hover:bg-[#F9F8F6] group/item transition-colors">
                  <ChevronRight className="h-4 w-4 text-[#C85A40] mt-0.5 flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity" strokeWidth={1.5} />
                  <div>
                    <h4 className="font-body text-sm font-medium text-[#111C16] group-hover/item:text-[#C85A40] transition-colors">{service.name}</h4>
                    <p className="font-body text-xs text-[#4A5D53]">{service.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Navbar = ({ onOpenModal }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F9F8F6]/80 border-b border-[#D5D1C8]/50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2">
           <img src="/assets/img/brand/logo-dark.png" alt="Trademark Application Hub" className="h-12" />
          </Link>
          <div className="hidden md:flex items-center space-x-8">
            {[{ to: '/', label: 'Home' }, { to: '/about', label: 'About' }].map(link => (
              <Link key={link.to} to={link.to} className={`font-body text-sm font-medium hover-underline-gold ${isActive(link.to) ? 'text-[#C85A40]' : 'text-[#111C16]'} hover:text-[#C85A40] transition-colors`}>{link.label}</Link>
            ))}
            <ServicesDropdown />
            {[{ to: '/blogs', label: 'Insights' }, { to: '/contact', label: 'Contact' }].map(link => (
              <Link key={link.to} to={link.to} className={`font-body text-sm font-medium hover-underline-gold ${isActive(link.to) ? 'text-[#C85A40]' : 'text-[#111C16]'} hover:text-[#C85A40] transition-colors`}>{link.label}</Link>
            ))}
            <Button  className="bg-[#C85A40] hover:bg-[#A64730] text-white btn-hover js-chat" data-testid="navbar-consultation-btn">
              Chat Now
            </Button>
          </div>
          <button className="md:hidden text-[#111C16]" onClick={() => setIsMenuOpen(!isMenuOpen)}>{isMenuOpen ? <X size={24} /> : <Menu size={24} />}</button>
        </div>
        {isMenuOpen && (
          <div className="md:hidden pb-4 border-t border-[#D5D1C8] mt-2 pt-4">
            <div className="flex flex-col space-y-3">
              <Link to="/" className="font-body text-sm text-[#111C16]" onClick={() => setIsMenuOpen(false)}>Home</Link>
              <Link to="/about" className="font-body text-sm text-[#111C16]" onClick={() => setIsMenuOpen(false)}>About</Link>
              <div>
                <button onClick={() => setIsServicesOpen(!isServicesOpen)} className="flex items-center font-body text-sm text-[#111C16] w-full">Services <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} /></button>
                {isServicesOpen && (
                  <div className="ml-4 mt-2 flex flex-col space-y-2">
                    <Link to="/services/trademark" className="font-body text-sm text-[#4A5D53]" onClick={() => setIsMenuOpen(false)}>Trademark</Link>
                    <Link to="/services/copyright" className="font-body text-sm text-[#4A5D53]" onClick={() => setIsMenuOpen(false)}>Copyright</Link>
                    <Link to="/services/patent" className="font-body text-sm text-[#4A5D53]" onClick={() => setIsMenuOpen(false)}>Patent</Link>
                    <Link to="/services/company-formation" className="font-body text-sm text-[#4A5D53]" onClick={() => setIsMenuOpen(false)}>Company Formation</Link>
                  </div>
                )}
              </div>
              <Link to="/blogs" className="font-body text-sm text-[#111C16]" onClick={() => setIsMenuOpen(false)}>Insights</Link>
              <Link to="/contact" className="font-body text-sm text-[#111C16]" onClick={() => setIsMenuOpen(false)}>Contact</Link>
              <Button onClick={() => { setIsMenuOpen(false); onOpenModal && onOpenModal('Consultation - Navbar Mobile'); }} className="bg-[#C85A40] hover:bg-[#A64730] text-white btn-hover w-full">Free Consultation</Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
